// Hash de CONTEÚDO calculado no cliente — pra decidir se uma surface do catálogo
// mudou de verdade, sem depender de nada que o servidor exponha.
//
// ── Por que existe ──
// O disjuntor de gravação (catalog-write-breaker) só grava a PRIORIDADE
// (`liveChannels` + categorias) num aparelho sob pressão de cota, e precisa
// saber se ela mudou. As duas chaves que vêm do servidor não servem pra isso:
//  • o hash do ETag é do snapshot INTEIRO (e nem é legível sem CORS exposto);
//  • o `generatedAt` muda a cada RECONSTRUÇÃO do snapshot no servidor — a cada
//    5 min quando degradado, a cada bump de `cat:ver` (escopo compartilhado), e
//    no HXTV a cada ciclo do `live_streams`: até ~48 por dia — mesmo com a
//    prioridade idêntica.
// Comparar por qualquer um dos dois regravava ~400 KB a cada poll. Só o hash da
// própria fatia distingue "o servidor reconstruiu" de "a prioridade mudou".
//
// ── O algoritmo: cyrb53 ──
// Domínio público (bryc). 53 bits de saída (cabe exato num `number`), duas
// correntes de 32 bits misturadas: colisão ~1 em 9·10^15 por par, contra ~48
// comparações por dia. Rápido: 2 `Math.imul` por caractere, sem alocação. Um
// `liveChannels` de ~391 KB custa um loop de ~400 mil iterações.
//
// Colisão teria custo LIMITADO: a fatia nova seria pulada até a próxima mudança
// dela — e o caminho do vídeo re-deriva a fonte do catálogo vivo a cada clique,
// então um dado local velho nunca vira URL morta.
//
// ── Serialização estável ──
// `JSON.stringify` do valor como recebido: mesma entrada ⇒ mesma string (a
// ordem das chaves é a do JSON de origem). Se o servidor reordenar itens sem
// mudar conteúdo, o hash muda e a fatia é regravada — erro no lado seguro.
//
// COMPAT Chr38: `Math.imul` é Chr28+. Sem BigInt, sem TextEncoder.
//
// ── A FORMA do laço importa no motor antigo (review do `c8268b84`, P2-4) ──
// Medido no Chromium 38 e 53 GENUÍNOS, com o código como a TV o recebe
// (esbuild + babel do plugin-legacy): o cyrb53 na forma de referência levava
// ~98 ms (Chr38) e ~81 ms (Chr53) pras 4 surfaces da prioridade (~460 KB), no
// silício do PC de desenvolvimento, com a máquina ~1,5-2x carregada. Nesta
// forma, 5,0 ms e 5,6 ms. Mesma saída, bit a bit (vetores fixos no teste, tirados
// da forma de referência). O que muda:
//  • multiplicadores como LITERAIS int32 (`2654435761 | 0` = -1640531535). O
//    `Math.imul` converte pra int32 de qualquer jeito, então o resultado é o
//    mesmo; mas o literal acima de 2^31 é um double, e o V8 antigo não otimiza
//    o laço com ele;
//  • sem parâmetro default: o babel do legado o transforma em `arguments[1]`;
//  • a finalização numa função à parte, fora do laço quente.
// No Chrome moderno não muda nada (~2 ms nos dois). Não "limpe" de volta pra
// forma de referência sem medir no Chromium antigo.

function misturar(str: string, h1: number, h2: number): number {
  for (let i = 0, n = str.length; i < n; i++) {
    const ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, -1640531535) // 2654435761 | 0
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  return finalizar(h1, h2)
}

function finalizar(a: number, b: number): number {
  let h1 = Math.imul(a ^ (a >>> 16), -2048144789) // 2246822507 | 0
  h1 ^= Math.imul(b ^ (b >>> 13), -1028477387) // 3266489909 | 0
  let h2 = Math.imul(b ^ (b >>> 16), -2048144789)
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), -1028477387)
  return 4294967296 * (2097151 & h2) + (h1 >>> 0)
}

/** cyrb53 (domínio público, github.com/bryc/code). Hash de 53 bits de uma string. */
export function cyrb53(str: string, seed?: number): number {
  const s = seed === undefined ? 0 : seed
  return misturar(str, (0xdeadbeef ^ s) | 0, (0x41c6ce57 ^ s) | 0)
}

/**
 * Hash do CONTEÚDO de um valor serializável. `undefined` (surface ausente no
 * corpo) tem hash próprio e estável — nunca lança.
 */
export function hashConteudo(valor: unknown): string {
  const texto = JSON.stringify(valor)
  return cyrb53(texto === undefined ? '\u0000undefined' : texto).toString(36)
}
